import React, { useState, useEffect, useRef } from 'react';
import { isPinReleased, onPinRelease, findScrollContainer } from '../utils/scrollPin';
import styles from '../styles/AnimatedMessage.module.css';

/**
 * AnimatedMessage - Sentence-by-sentence animated text display
 * - Shows each sentence in its own bubble
 * - Reveals bubbles one at a time as sentences complete
 * - Click anywhere to reveal all sentences instantly
 * - Single avatar at the top for the entire message
 */
function AnimatedMessage({ content, onComplete, skipAnimation = false }) {
  const [sentences, setSentences] = useState([]);
  const [revealedSentences, setRevealedSentences] = useState(0);
  const [currentWords, setCurrentWords] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isFullyRevealed, setIsFullyRevealed] = useState(false);
  const hasCalledCompleteRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const containerRef = useRef(null);
  const lastBubbleRef = useRef(null);
  // The bubble currently being typed (per-word reveal). Distinct from
  // lastBubbleRef which points to the most recently *completed* sentence.
  const animatingBubbleRef = useRef(null);

  // Keep onComplete ref up to date without triggering re-renders
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Parse HTML content into sentences
  useEffect(() => {
    // Create a temporary div to strip HTML tags
    // formatResponse turns newlines into <br>, and textContent drops <br>
    // entirely — "goals.<br><br>So" became "goals.So", gluing two lines into
    // one bubble with no space. Turn them back into real newlines first.
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content.replace(/<br\s*\/?>/gi, '\n');

    // Get plain text content
    const text = tempDiv.textContent || tempDiv.innerText || '';

    // Every non-empty line is its own bubble; within a line, split into
    // sentences (periods followed by whitespace)
    const sentenceArray = text
      .split(/\n+/)
      .flatMap(line => line.split(/(?<=\.)\s+/))
      .map(s => s.trim())
      .filter(s => s.length > 0);

    setSentences(sentenceArray);

    // If skipAnimation is true, immediately show all sentences
    if (skipAnimation) {
      setRevealedSentences(sentenceArray.length);
      setCurrentWords([]);
      setCurrentWordIndex(0);
      setIsFullyRevealed(true);
      hasCalledCompleteRef.current = false;
    } else {
      setRevealedSentences(0);
      setCurrentWords([]);
      setCurrentWordIndex(0);
      setIsFullyRevealed(false);
      hasCalledCompleteRef.current = false;
    }
  }, [content, skipAnimation]);

  // Animate current sentence word by word
  useEffect(() => {
    // Don't start animation until sentences are parsed
    if (sentences.length === 0) {
      return;
    }

    if (isFullyRevealed || revealedSentences >= sentences.length) {
      // Only call onComplete once when animation naturally completes
      if (revealedSentences === sentences.length && onCompleteRef.current && !hasCalledCompleteRef.current) {
        hasCalledCompleteRef.current = true;
        onCompleteRef.current();
      }
      return;
    }

    // If starting a new sentence, split it into words
    if (currentWords.length === 0 && revealedSentences < sentences.length) {
      const words = sentences[revealedSentences].trim().split(/\s+/).filter(w => w.length > 0);
      setCurrentWords(words);
      setCurrentWordIndex(0);
      return;
    }

    // Reveal words one at a time
    if (currentWordIndex < currentWords.length) {
      const delay = currentWordIndex === 0 ? 100 : 150;
      const timer = setTimeout(() => {
        setCurrentWordIndex(prev => prev + 1);
      }, delay);
      return () => clearTimeout(timer);
    }

    // Sentence complete - move to next sentence
    if (currentWordIndex === currentWords.length && currentWords.length > 0) {
      const timer = setTimeout(() => {
        setRevealedSentences(prev => prev + 1);
        setCurrentWords([]);
        setCurrentWordIndex(0);
      }, 400); // Brief pause before next sentence
      return () => clearTimeout(timer);
    }
  }, [revealedSentences, currentWords, currentWordIndex, sentences, isFullyRevealed]);

  // While the animation runs, keep the chat scroll container pinned to the
  // bottom — TWEENED, not jumped (product direction: "no jumps in the
  // scroll, tween in between positions"). A single rAF follower eases
  // scrollTop toward the bottom each frame; the target is re-read every
  // frame, so content growing mid-tween just extends the same glide instead
  // of queueing competing scrolls. The ancestor walk must require a REAL
  // scroll container (overflow-y auto/scroll): messagesContainer flex-shrinks
  // and its content pokes out with overflow visible, so a bare
  // scrollHeight > clientHeight test stopped there and the scrollTop
  // assignment was a silent no-op — autoscroll looked "gone".
  const scrollFollowerRef = useRef({ raf: null, el: null });
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    let el = node.parentElement;
    while (el) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) break;
      el = el.parentElement;
    }
    if (!el) return;

    // She scrolled up to read history — never drag her back down. ChatArea
    // re-arms the pin once she returns to the bottom herself.
    if (isPinReleased(el)) return;

    const follower = scrollFollowerRef.current;
    follower.el = el;

    // Reduced motion: land instantly, no glide.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.scrollTop = el.scrollHeight;
      return;
    }

    if (follower.raf) return; // already gliding — it reads the target live

    const step = () => {
      // Released mid-glide: stop on the spot instead of fighting her scroll.
      if (isPinReleased(follower.el)) {
        follower.raf = null;
        return;
      }
      const target = follower.el.scrollHeight - follower.el.clientHeight;
      const delta = target - follower.el.scrollTop;
      if (Math.abs(delta) < 1) {
        follower.el.scrollTop = target;
        follower.raf = null;
        return;
      }
      // exponential ease-out: cover ~18% of the remaining distance per frame
      follower.el.scrollTop += delta * 0.18;
      follower.raf = requestAnimationFrame(step);
    };
    follower.raf = requestAnimationFrame(step);
  }, [revealedSentences, currentWordIndex, isFullyRevealed]);

  useEffect(() => {
    const follower = scrollFollowerRef.current;
    return () => {
      if (follower.raf) cancelAnimationFrame(follower.raf);
    };
  }, []);

  // Click to reveal all sentences instantly
  const handleClick = () => {
    if (!isFullyRevealed) {
      setIsFullyRevealed(true);
      setRevealedSentences(sentences.length);
      setCurrentWords([]);
      setCurrentWordIndex(0);

      // Call onComplete immediately when user clicks to skip
      if (onCompleteRef.current && !hasCalledCompleteRef.current) {
        hasCalledCompleteRef.current = true;
        onCompleteRef.current();
      }
    }
  };

  // Scrolling up mid-reveal means "let me read something else" — treat it like
  // a tap: finish the reveal instantly so the animation never holds her back.
  // The ref keeps the subscription stable while handleClick changes per render.
  const revealAllRef = useRef(handleClick);
  useEffect(() => {
    revealAllRef.current = handleClick;
  });
  const hasSentences = sentences.length > 0;
  useEffect(() => {
    // containerRef only exists once there is something to render
    if (!hasSentences) return undefined;
    const scrollEl = findScrollContainer(containerRef.current);
    return onPinRelease(scrollEl, () => revealAllRef.current());
  }, [hasSentences]);

  if (sentences.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={styles.container}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      <div className={styles.avatar}>S</div>
      <div className={styles.bubblesWrapper}>
        {/* Render completed sentence bubbles */}
        {sentences.slice(0, revealedSentences).map((sentence, index) => (
          <div
            key={index}
            ref={index === revealedSentences - 1 ? lastBubbleRef : null}
            className={`${styles.bubble} ${styles.complete}`}
          >
            {sentence}
          </div>
        ))}

        {/* Render current sentence being typed */}
        {!isFullyRevealed && revealedSentences < sentences.length && currentWords.length > 0 && (
          <div ref={animatingBubbleRef} className={`${styles.bubble} ${styles.animating}`}>
            {currentWords.slice(0, currentWordIndex).map((word, index) => {
              const isLast = index === currentWordIndex - 1;
              // Real space characters between words (not CSS margins) so the
              // animated line has exactly the metrics of the finished text.
              return (
                <span
                  key={index}
                  className={`${styles.word} ${styles.visible} ${isLast ? styles.current : ''}`}
                >
                  {word}
                  {isLast ? '' : ' '}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {!isFullyRevealed && revealedSentences < sentences.length && (
        <div className={styles.hint}>
          Tap to reveal full message
        </div>
      )}
    </div>
  );
}

export default AnimatedMessage;
